import * as React from 'react';

import { Input, type InputProps } from '@/components/ui/Input/Input';
import { cn } from '@/utils';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PasswordInputProps extends InputProps {
  revealButton?: {
    showAriaLabel?: string;
    hideAriaLabel?: string;
  };
}

const PasswordInput = ({ className, revealButton, ...props }: PasswordInputProps) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'passwordInput',
  });

  const isError = props['aria-invalid'] === true || !!props.isError;

  const [inputType, setInputType] = React.useState('password');

  const toggleType = () => {
    setInputType((prev) => (prev === 'password' ? 'text' : 'password'));
  };

  const showAriaLabel = revealButton?.showAriaLabel ?? t('toggleVisibility.show.ariaLabel');
  const hideAriaLabel = revealButton?.hideAriaLabel ?? t('toggleVisibility.hide.ariaLabel');

  return (
    <div className='relative'>
      <Input
        className={cn(className, 'pr-10')}
        aria-invalid={isError ?? undefined}
        {...props}
        type={inputType}
        endIcon={
          <button
            type='button'
            aria-label={inputType === 'password' ? showAriaLabel : hideAriaLabel}
            onClick={toggleType}
            className={cn('text-text-default-secondary flex h-full items-center', props.disabled && 'cursor-not-allowed opacity-50')}
            disabled={props.disabled}
          >
            {inputType === 'password' ? (
              <EyeIcon size={24} strokeWidth={1.5} className={cn(isError && 'text-text-error-primary')} />
            ) : (
              <EyeOffIcon size={24} strokeWidth={1.5} className={cn(isError && 'text-text-error-primary')} />
            )}
          </button>
        }
      />
    </div>
  );
};
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
